/**
 * ActorFilter — the audit log's Who filter (TRA-1700).
 *
 * A searchable list of everyone in this team's log: people by email, API
 * keys and jobs by id. Typing narrows the list; a pick filters to exactly
 * that one. The typed text itself is offered too, so part of a name
 * ("jake") finds every match, including anyone past the list's cap.
 */

import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { AuditActors } from '@/hooks/useAuditEvents'
import { cn } from '@/lib/utils'

import { type ActorChoice, actorChoiceLabel, matchingPeople } from './auditQuery'

interface ActorFilterProps {
  id: string
  choice: ActorChoice
  /** Who is in this team's log. */
  actors: AuditActors
  onChange: (choice: ActorChoice) => void
}

const EMPTY_TEXT: Record<AuditActors['state'], string> = {
  loading: 'Loading.',
  failed: 'The list did not load. Type part of an email or id to filter.',
  ready: 'No one is in this log yet.',
}

export function ActorFilter({ id, choice, actors, onChange }: ActorFilterProps): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const trimmed = typed.trim()
  const unset = choice.actor === '' && choice.actorSearch === ''
  const shown = matchingPeople(actors.state === 'ready' ? actors.people : [], trimmed)

  const pick = (next: ActorChoice): void => {
    onChange(next)
    setTyped('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('h-8 w-full justify-between text-xs font-normal', unset && 'text-muted-foreground')}
        >
          <span className="truncate">{actorChoiceLabel(choice)}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        {/* The list filters itself with the server's rule (part of an
            email, any case), so the typed-text option stays first. */}
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type part of an email or id" value={typed} onValueChange={setTyped} />
          <CommandList>
            {/* Typed text always offers itself, so this shows only with
                nothing typed. */}
            <CommandEmpty>{EMPTY_TEXT[actors.state]}</CommandEmpty>
            <CommandGroup>
              {trimmed !== '' && (
                <CommandItem
                  value={`matches for ${trimmed}`}
                  onSelect={() => {
                    pick({ actor: '', actorSearch: trimmed })
                  }}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', choice.actorSearch === trimmed ? 'opacity-100' : 'opacity-0')}
                  />
                  {actorChoiceLabel({ actor: '', actorSearch: trimmed })}
                </CommandItem>
              )}
              {!unset && trimmed === '' && (
                <CommandItem
                  value="anyone"
                  onSelect={() => {
                    pick({ actor: '', actorSearch: '' })
                  }}
                >
                  Anyone
                </CommandItem>
              )}
              {shown.map((person) => (
                <CommandItem
                  key={person}
                  value={person}
                  onSelect={() => {
                    pick({ actor: person, actorSearch: '' })
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', choice.actor === person ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{person}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
